import styled, { css } from "styled-components";

import { md3CardRadius, md3PressTransition } from "../../styles/md3Components";
import { fontSizes } from "../../styles/typography";

export type Md3CardColor = "primary" | "secondary" | "surface" | "gradient";

const colorStyles = {
  primary: css`
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    border: 2px solid rgba(255, 255, 255, 0.2);
  `,
  secondary: css`
    background: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.white};
    border: 2px solid rgba(255, 255, 255, 0.2);
  `,
  surface: css`
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    border: 1px solid ${({ theme }) => theme.colors.border};
    box-shadow: ${({ theme }) => theme.shadows.md3Level1};
  `,
  gradient: css`
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.primaryDark},
      ${({ theme }) => theme.colors.primary},
      ${({ theme }) => theme.colors.primaryLight}
    );
    color: ${({ theme }) => theme.colors.white};
    border: 2px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 12px 24px ${({ theme }) => theme.colors.primaryAlpha20};
  `,
};

export const Md3CardRoot = styled.article<{
  $color: Md3CardColor;
  $interactive?: boolean;
}>`
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  padding: ${({ theme }) => theme.spacing.lg}px;
  position: relative;
  overflow: hidden;
  transition: ${({ theme }) => md3PressTransition(theme)};
  ${({ $color }) => colorStyles[$color]}

  ${({ $interactive }) =>
    $interactive &&
    css`
      cursor: pointer;

      &:hover {
        transform: translateY(-2px);
        box-shadow: ${({ theme }) => theme.shadows.cardHover};
      }

      &:active {
        transform: scale(0.98);
      }

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.colors.focus};
        outline-offset: 3px;
      }
    `}
`;

export const Md3CardBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.25);
  padding: 8px 16px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  font-size: ${fontSizes.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const Md3CardTitle = styled.h2`
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.xl}px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin: 0 0 8px;
  letter-spacing: -0.5px;
`;

export const Md3CardSubtitle = styled.p`
  font-size: ${fontSizes.base}px;
  opacity: 0.95;
  margin: 0 0 24px;
  line-height: 1.5;
  max-width: 90%;
`;

export const Md3CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

export const Md3CardAction = styled.button`
  background: rgba(255, 255, 255, 0.25);
  padding: 12px 24px;
  border-radius: ${({ theme }) => theme.borderRadius.full}px;
  color: inherit;
  font-size: ${fontSizes.base}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  cursor: pointer;
  flex: 1;
  justify-content: center;
  transition: background 0.2s ease;
  font-family: ${({ theme }) => theme.font.family};

  &:hover {
    background: rgba(255, 255, 255, 0.35);
  }
`;
